
import React, { useState } from 'react';
import ArticleTemplate from '@/components/ArticleTemplate';
import { useIsMobile } from '@/hooks/use-mobile';
import { 
  AreaChart, Area, LineChart, Line, PieChart, Pie, 
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell 
} from 'recharts';
import { NeumorphicCard } from '@/components/ui/neumorphic-card';
import { LainaCta } from '@/components/common/LainaCta';
import { BarChart4, PieChart as PieChartIcon, LineChart as LineChartIcon } from 'lucide-react';

// Data for the total cost chart
const totalCostData = [
  { name: '1%', Kokonaiskustannus: 253000 },
  { name: '3%', Kokonaiskustannus: 320000 },
  { name: '5%', Kokonaiskustannus: 400000 },
  { name: '7%', Kokonaiskustannus: 500000 },
];

// Data for the mortgage payment chart
const mortgagePaymentData = [
  { name: '1%', Lyhennys: 660, Korko: 167 },
  { name: '3%', Lyhennys: 550, Korko: 500 },
  { name: '5%', Lyhennys: 450, Korko: 850 },
  { name: '7%', Lyhennys: 350, Korko: 1170 },
];

// Data for the pie chart
const pieData = [
  { name: 'Lyhennys', value: 400 },
  { name: 'Korko', value: 300 },
  { name: 'Muut kulut', value: 300 },
];

// Function to generate a color based on the index
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

const RADIAN = Math.PI / 180;
const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }: any) => {
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central">
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="p-2 bg-white border border-gray-200 shadow-md rounded-md">
        <p className="font-semibold">{`${label} : ${payload[0].value}`}</p>
        {payload.map((item: any, index: number) => (
          <p key={`item-${index}`} className="text-gray-700">
            {item.name}: {item.value}
          </p>
        ))}
      </div>
    );
  }

  return null;
};

const KorotKokonaiskustannuksetArticlePage = () => {
  const [currentImageUrl, setCurrentImageUrl] = useState("/lovable-uploads/886a5105-052e-4546-9ba2-b653380a7c92.png");
  const isMobile = useIsMobile();
  const relatedArticles = [
    {
      id: "1",
      title: "Korkojen nousu ja inflaatio - näin suojaat talouttasi",
      slug: "korkojen-nousu-ja-inflaatio",
      description: "Käytännön neuvoja korkojen nousulta ja inflaatiolta suojautumiseen lainanottajan näkökulmasta. Miten varautua korkojen nousuun ja sen vaikutuksiin?"
    },
    {
      id: "3",
      title: "Asuntomarkkinoiden ennuste 2025 – kannattaako nyt ostaa vai myydä?",
      slug: "asuntomarkkinoiden-ennuste-2025-kannattaako-nyt-ostaa-vai-myyda",
      description: "Millaisessa tilanteessa asuntomarkkinat ovat vuonna 2025? Tässä artikkelissa analysoimme asuntomarkkinoiden nykytilaa ja tulevaisuuden näkymiä."
    }
  ];

  // Define article content separately to avoid duplication
  const articleContent = (
    <div className="article-content">
      <p>
        Korkotason vaikutus lainojen kokonaiskustannuksiin on merkittävä. Tässä artikkelissa näytämme konkreettisesti, miten eri korkoprosentit vaikuttavat erilaisiin lainoihin.
      </p>
      <p>
        Käsittelemme asuntolainoja, kulutusluottoja ja muita lainatyyppejä, jotta saat kattavan kuvan korkojen merkityksestä.
      </p>
      
      <h2 id="korko-vaikutus">Korkojen vaikutus lainan kokonaiskustannuksiin</h2>
      <p>
        Lainan kokonaiskustannukset muodostuvat lainapääomasta ja laina-aikana kertyvistä koroista. 
        Korko vaikuttaa suoraan siihen, kuinka paljon joudut maksamaan lainasta takaisin.
      </p>

      <div className="flex flex-col gap-6 mb-10">
        <NeumorphicCard
          title="Lainan kokonaiskustannukset eri korkoprosenteilla"
          icon={<BarChart4 className="w-6 h-6 text-white" />}
          description="200 000 € asuntolainan kokonaiskustannukset 25 vuodessa eri korkoprosenteilla"
        >
          <div className={`${isMobile ? 'h-64' : 'h-80 md:h-96'} mt-4`}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={totalCostData}
                margin={isMobile ? 
                  { top: 10, right: 0, left: -10, bottom: 30 } : 
                  { top: 20, right: 30, left: 30, bottom: 50 }}
              >
                <defs>
                  <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#8884d8" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Area type="monotone" dataKey="Kokonaiskustannus" stroke="#8884d8" fillOpacity={1} fill="url(#colorUv)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <p className="text-sm mt-6 text-gray-600">
            Kuvaaja näyttää, miten asuntolainan kokonaiskustannukset muuttuvat eri korkotasoilla.
          </p>
        </NeumorphicCard>

        <NeumorphicCard
          title="Lyhennyksen ja koron suhde laina-ajan alussa"
          icon={<LineChartIcon className="w-6 h-6 text-white" />}
          description="Miten 200 000 € lainan maksuerä jakautuu laina-ajan alussa?"
        >
          <p className="mb-4">
            Lainan alkuvaiheessa suurempi osa kuukausimaksusta menee korkoon. Laina-ajan edetessä 
            lyhennyksen osuus kasvaa, kun taas koron osuus pienenee lainapääoman pienentyessä.
            Annuiteettilainassa laina-ajan alussa 
            suurempi osa maksusta menee korkoon, mutta ajan myötä lyhennyksen osuus kasvaa.
          </p>

          <div className={`${isMobile ? 'h-64' : 'h-80 md:h-96'} mt-4`}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={mortgagePaymentData}
                margin={isMobile ? 
                  { top: 10, right: 0, left: -10, bottom: 30 } : 
                  { top: 20, right: 30, left: 30, bottom: 50 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="Lyhennys" stroke="#8884d8" activeDot={{ r: 8 }} />
                <Line type="monotone" dataKey="Korko" stroke="#82ca9d" />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <p className="text-sm mt-6 text-gray-600">
            Kuvaaja näyttää, miten kuukausimaksu jakautuu lyhennyksen ja koron välillä eri korkotasoilla.
          </p>
        </NeumorphicCard>
      </div>

      <p>
        Korko on lainan hinta, ja se ilmoitetaan yleensä vuosikorkona. Mitä alhaisempi korko, sitä edullisempi laina.
        Korko voi olla kiinteä tai vaihtuva. Kiinteä korko pysyy samana koko laina-ajan, kun taas vaihtuva korko voi muuttua markkinatilanteen mukaan.
      </p>
      <p>
        Lainan kokonaiskustannuksiin vaikuttavat myös muut tekijät, kuten lainanhoitomaksut, tilinhoitomaksut ja muut kulut.
        Siksi on tärkeää vertailla eri lainojen todellista vuosikorkoa, joka sisältää kaikki lainaan liittyvät kulut.
      </p>

      <h2 id="korko-vertailu">Korkojen vertailu ja säästömahdollisuudet</h2>
      <p>
        Korkojen vertailu on tärkeää, jotta voit löytää edullisimman lainan. Vertaile eri pankkien ja rahoituslaitosten tarjouksia.
        Voit myös käyttää lainavertailupalveluita, jotka auttavat sinua löytämään sopivan lainan.
      </p>
      <p>
        Pienikin korkoero voi tarkoittaa suurta säästöä laina-aikana. Esimerkiksi 0,5 %:n korkoero 200 000 euron asuntolainassa voi säästää tuhansia euroja.
      </p>

      <div className="w-full mb-10">
        <div className={`${isMobile ? 'w-full' : 'w-3/4 mx-auto'} bg-white p-4 rounded-lg shadow`}>
          <div className={`${isMobile ? 'h-64' : 'h-96'}`}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart margin={isMobile ? 
                { top: 0, right: 0, left: 0, bottom: 0 } : 
                { top: 10, right: 10, bottom: 10, left: 10 }}>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={renderCustomizedLabel}
                  outerRadius={isMobile ? 80 : 120}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {
                    pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))
                  }
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <p className="text-sm mt-2 text-gray-600 text-center">
            Kuvaaja näyttää lainan kokonaiskustannusten jakautumisen eri tekijöihin.
          </p>
        </div>
      </div>

      <p>
        Säästömahdollisuudet eivät rajoitu vain korkoihin. Voit säästää myös kilpailuttamalla lainanhoitomaksut ja muut kulut.
        Neuvottele pankin kanssa lainaehdoista ja pyri saamaan mahdollisimman edullinen laina.
      </p>

      <div className="bg-gradient-to-r from-cyan-50 to-blue-50 p-6 my-8 rounded-xl border border-cyan-200 shadow-sm">
        <div className="flex flex-col md:flex-row items-center gap-6">
          <div className="flex-shrink-0">
            <img 
              src="/lovable-uploads/886a5105-052e-4546-9ba2-b653380a7c92.png" 
              alt="Northmill Bank logo" 
              className="h-16 w-auto object-contain"
            />
          </div>
          <div className="flex-grow text-center md:text-left">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Northmill Bank - Kilpailukykyiset korot ja läpinäkyvät ehdot</h3>
            <p className="text-gray-700 mb-4">
              Northmill Bank tarjoaa kilpailukykyisiä lainakorkoja ja selkeät, läpinäkyvät lainaehdot. Digitaalisessa pankkipalvelussa 
              näet helposti lainan todelliset kustannukset ilman yllätyksiä.
            </p>
            <ul className="text-sm text-gray-600 space-y-1 mb-4">
              <li>✓ Kilpailukykyiset lainakorot</li>
              <li>✓ Läpinäkyvät kustannukset</li>
              <li>✓ Ei piilotettuja maksuja</li>
              <li>✓ Todellinen vuosikorko selkeästi esillä</li>
            </ul>
            <p className="text-xs text-gray-500">
              Northmill Bank auttaa sinua ymmärtämään lainasi todelliset kustannukset ja löytämään edullisimman rahoitusratkaisun.
            </p>
          </div>
        </div>
      </div>

      <div className="mt-8 mb-6">
        <LainaCta 
          text="Kilpailuta lainasi ja löydä paras korko" 
          to="/kilpailuta" 
          variant="primary" 
          size="lg" 
          className="w-full md:w-auto"
        />
      </div>
    </div>
  );

  return (
    <ArticleTemplate
      title="Näin korot vaikuttavat lainan kokonaiskustannuksiin"
      description="Korkotason vaikutus lainojen kokonaiskustannuksiin on merkittävä. Tässä artikkelissa näytämme konkreettisesti, miten eri korkoprosentit vaikuttavat erilaisiin lainoihin."
      date="2023-10-15"
      readTime="10 min"
      category="Korot"
      imageUrl={currentImageUrl}
      relatedArticles={relatedArticles}
      content={articleContent}
    />
  );
};

export default KorotKokonaiskustannuksetArticlePage;
